import React, { useState, useEffect } from 'react';
import {
  Spin,
  Empty,
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  Select,
  Space,
  Alert,
  Tag,
  Checkbox,
} from 'antd';

import {
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

import { baseUrl_SPB } from '../../../BaseUrl';
import axios from 'axios';

const LEAVE_TYPES = [
  { label: 'Annual Leave', value: 'AL' },
  { label: 'Personal Leave', value: 'PL' },
  { label: 'Long Service Leave', value: 'LSL' },
];

const LeavePanel = ({
  employeeId,
  token,
  isUserManager,
}) => {

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  // WATCH ALL DAY FIELD
  const isAllDay = Form.useWatch('isAllDay', form);

  useEffect(() => {
    fetchLeaves();
  }, [employeeId]);

  const fetchLeaves = async () => {

    if (!token || !employeeId) return;

    setLoading(true);
    setError('');

    try {

      const response = await fetch(
        `${baseUrl_SPB}/api/v1/unavailability/getall?staffId=${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {

        const data = await response.json();

        setLeaves(Array.isArray(data) ? data : []);
      }

    } catch (err) {

      console.error(err);

      setLeaves([]);

    } finally {

      setLoading(false);
    }
  };

  const handleAddLeave = async () => {

    try {

      const values = await form.validateFields();

      setSaving(true);
      setError('');

      

      const payload = {
        id: null,

        type: 'L',

        staffId: employeeId,

        startDate: values.startDate.format('DD-MM-YYYY'),

        endDate: values.endDate.format('DD-MM-YYYY'),

        starttime:
            isAllDay === 'Y'
            ? '00:00'
            : values.startTime?.format('HH:mm') || '00:00',

        endtime:
          isAllDay === 'Y'
            ? '23:59'
            : values.endTime?.format('HH:mm') || '23:59',

        leaveType: values.leaveType,

        approvalStatus:
          isUserManager ? 'Approved' : 'Pending',

        allDay: isAllDay,
      };

      await axios.post(
        `${baseUrl_SPB}/api/v1/unavailability/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setModalOpen(false);

      form.resetFields();

      await fetchLeaves();

    } catch (err) {

      if (err?.errorFields) return;

      console.error(err);

      setError('Failed to save leave request.');

    } finally {

      setSaving(false);
    }
  };

  const handleDelete = async (id) => {

    Modal.confirm({

      title: 'Delete leave record',

      content: 'Are you sure?',

      okText: 'Delete',

      cancelText: 'Cancel',

      okButtonProps: {
        danger: true,
      },

      onOk: async () => {

        try {

          await axios.get(
            `${baseUrl_SPB}/api/v1/unavailability/delete?id=${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          await fetchLeaves();

        } catch (err) {

          console.error(err);

          setError('Failed to delete leave record.');
        }
      },
    });
  };

  const getLeaveTypeLabel = (type) => {

    return (
      LEAVE_TYPES.find((t) => t.value === type)?.label || type
    );
  };

  const getStatusColor = (status) => {

    if (status === 'Approved') return 'green';

    if (status === 'Pending') return 'orange';

    if (status === 'Rejected') return 'red';

    return 'default';
  };

  const columns = [
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType',

      render: (type) => getLeaveTypeLabel(type),
    },

    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },

    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },

    {
      title: 'Time',
      key: 'time',

      render: (_, record) =>
        `${record.starttime || '00:00'} - ${record.endtime || '23:59'}`,
    },

    {
      title: 'Status',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',

      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status || 'Pending'}
        </Tag>
      ),
    },

    {
      title: '',
      key: 'actions',
      width: 60,

      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
        />
      ),
    },
  ];

  return (

    <Space
      direction="vertical"
      style={{ width: '100%' }}
      size={12}
    >

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          closable
          onClose={() => setError('')}
        />
      )}

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)}
      >
        Add leave request
      </Button>

      <Spin spinning={loading}>

        {leaves.length === 0 ? (

          <Empty description="No leave records" />

        ) : (

          <Table
            rowKey={(rec) => rec.id || rec.startDate}
            dataSource={leaves}
            columns={columns}
            pagination={false}
            size="small"
          />
        )}

      </Spin>

      <Modal
        open={modalOpen}
        title="Add Leave Request"
        okText="Save"
        cancelText="Cancel"
        onOk={handleAddLeave}
        confirmLoading={saving}
        onCancel={() => {

          setModalOpen(false);

          form.resetFields();
        }}
      >

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isAllDay: true,
          }}
        >

          <Form.Item
            name="leaveType"
            label="Leave type"
            rules={[
              {
                required: true,
                message: 'Required',
              },
            ]}
          >
            <Select options={LEAVE_TYPES} />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start date"
            rules={[
              {
                required: true,
                message: 'Required',
              },
            ]}
          >
            <DatePicker
              format="DD-MM-YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End date"
            rules={[
              {
                required: true,
                message: 'Required',
              },
            ]}
          >
            <DatePicker
              format="DD-MM-YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="isAllDay"
            valuePropName="checked"
          >
            <Checkbox>
              All day
            </Checkbox>
          </Form.Item>

          {!isAllDay && (
            <>
              <Form.Item
                name="startTime"
                label="Start time"
              >
                <DatePicker
                  picker="time"
                  format="HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="endTime"
                label="End time"
              >
                <DatePicker
                  picker="time"
                  format="HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </>
          )}

        </Form>

      </Modal>

    </Space>
  );
};

export default LeavePanel;